import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import { getOverrideSettings } from 'modules/shared-menu/api';
import { OverrideSettingsResponse, OfferingPlatformScope } from 'modules/shared-menu/types';

import { useSelectionInfoContext } from '../../../contexts/SelectionInfoContext';
import { mapOverrideSettingToOfferKeys } from '../constants';

type LoadOverrideSettingsProps = {
  ownerScope: OfferingPlatformScope | undefined;
  enabled: boolean;
};

export const useLoadOverrideSettings = ({ ownerScope, enabled }: LoadOverrideSettingsProps) => {
  const queryKey = ['shared-menu-settings', [ownerScope?.id]];

  const { data: overrideSettings, isInitialLoading: loadingOverrideSettings } = useQuery(
    queryKey,
    () => (ownerScope ? getOverrideSettings(ownerScope) : null),
    {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      staleTime: Infinity,
      enabled: enabled && !!ownerScope,
    },
  );

  return { overrideSettings, loadingOverrideSettings };
};

export const useOverrideSettings = () => {
  const { vlmeConfig, selection } = useSelectionInfoContext();

  const queryClient = useQueryClient();
  const queryKey = ['shared-menu-settings', [selection.scope.id]];

  const overrideSettings = queryClient.getQueryData<OverrideSettingsResponse>(queryKey);

  if (!overrideSettings && vlmeConfig && vlmeConfig.isSingleVenue === false) {
    throw new Error('No override settings data found, this should not happen!');
  }

  const offerAttrsEnabledForOverride = useMemo(() => {
    if (!overrideSettings) {
      return [];
    }

    const enabledSettings: string[] = Object.keys(overrideSettings).filter(
      (key: keyof OverrideSettingsResponse) => overrideSettings[key] === true,
    );

    return enabledSettings.flatMap((setting: keyof Omit<OverrideSettingsResponse, 'scope'>) => [
      ...mapOverrideSettingToOfferKeys[setting],
    ]);
  }, [overrideSettings]);

  return { overrideSettings, offerAttrsEnabledForOverride };
};
